import { X, Pin, ChevronRight, Loader2, Zap, Clock, TrendingUp, Target } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface Category {
  id?: string;
  name?: string;
  label?: string;
  count: number;
  type?: string;
}

interface Source {
  id: string;
  name: string;
  abbr?: string;
}

interface PulseItem {
  id: string;
  insightId: number;
  title: string;
  source: string;
  sourceType: "municipal" | "regional" | "provincial";
  category: string;
  date: string;
  whatChanged: string;
  whoWhereAffects: string;
  impactOnYou?: string;
  suggestedAction?: string;
  status: string;
  confidence: number;
  isRelevant?: boolean;
  // New personalized fields
  tldrShort?: string;
  magnitude?: string;
  scope?: string;
  timeSensitivity?: string;
}

const DISMISS_ASKED_KEY = 'dismissAskedPulses';

function getDismissAskedPulseIds(): Set<string> {
  try {
    const stored = localStorage.getItem(DISMISS_ASKED_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

interface PulseListModalProps {
  isOpen: boolean;
  onClose: () => void;
  source?: Source;
  category?: Category;
  pulseItems: PulseItem[];
  onPulseClick: (pulse: PulseItem) => void;
  pinnedIds: Set<string | number>;
  onTogglePin: (pulse: PulseItem) => void;
  isLoading?: boolean;
}

// Impact indicator component
function ImpactIndicator({ magnitude, scope, timeSensitivity }: { 
  magnitude?: string; 
  scope?: string; 
  timeSensitivity?: string;
}) {
  const getImpactColor = () => {
    if (magnitude === 'major') return 'text-red-500';
    if (magnitude === 'moderate') return 'text-amber-500';
    return 'text-green-500';
  };

  const getImpactLabel = () => {
    if (magnitude === 'major') return 'High Impact';
    if (magnitude === 'moderate') return 'Medium Impact';
    return 'Low Impact';
  };

  const getTimeColor = () => {
    if (timeSensitivity === 'high') return 'text-red-500';
    if (timeSensitivity === 'medium') return 'text-amber-500';
    return 'text-neutral-400';
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {magnitude && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn("flex items-center gap-1", getImpactColor())}>
                <Zap className="h-3 w-3" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  {getImpactLabel()}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">
                <strong>Magnitude:</strong> {magnitude} • <strong>Scope:</strong> {scope || 'N/A'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
        {timeSensitivity && timeSensitivity !== 'low' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn("flex items-center gap-1", getTimeColor())}>
                <Clock className="h-3 w-3" />
                <span className="text-[10px] font-semibold uppercase">Urgent</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Time-sensitive action required</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

export default function PulseListModal({
  isOpen,
  onClose,
  source,
  category,
  pulseItems,
  onPulseClick,
  pinnedIds,
  onTogglePin,
  isLoading = false,
}: PulseListModalProps) {
  // Format category: remove dashes, capitalize words
  const formatCategory = (categoryName: string) => {
    return categoryName
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  const rawCategoryName = category?.name || category?.label || "Items";
  const categoryName = formatCategory(rawCategoryName);
  const sourceAbbr = source?.abbr;
  const sourceName = source?.name || "";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden border-none shadow-2xl bg-white dark:bg-neutral-900">
        <DialogHeader className="sr-only">
          <DialogTitle>Pulses for {categoryName}</DialogTitle>
          <DialogDescription>
            View and manage pulses in {sourceName} - {categoryName}
          </DialogDescription>
        </DialogHeader>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                "text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full",
                source?.abbr
                  ? "bg-gradient-to-r from-primary/20 to-primary/10 text-primary"
                  : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              )}
            >
              {sourceAbbr || sourceName}
            </span>
            <span className="text-neutral-300 dark:text-neutral-600">/</span>
            <span className="font-semibold text-neutral-900 dark:text-white">
              {categoryName}
            </span>
          </div>
        </div>

        {/* Pulse List */}
        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3 bg-neutral-50 dark:bg-neutral-950">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : pulseItems.length === 0 ? (
            <div className="text-center py-12 text-neutral-400">
              <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No pulses found</p>
              <p className="text-sm mt-1">Check back later for updates</p>
            </div>
          ) : (() => {
            const viewedIds = getDismissAskedPulseIds();
            return pulseItems.map((pulse) => {
            const isPinned = pinnedIds.has(pulse.insightId);
            const isLowRelevance = pulse.isRelevant === false;
            const isNewPulse = !viewedIds.has(pulse.id);
            
            return (
              <div
                key={pulse.id}
                className={cn(
                  "relative border rounded-xl p-4 cursor-pointer transition-all group",
                  "hover:shadow-lg hover:-translate-y-0.5",
                  isLowRelevance 
                    ? "bg-neutral-100 dark:bg-neutral-900/50 border-neutral-200 dark:border-neutral-800 opacity-70 hover:opacity-100"
                    : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 hover:border-primary/40",
                  !isLowRelevance && isNewPulse && "border-2 border-primary"
                )}
              >
                {/* Impact indicator bar */}
                {pulse.magnitude === 'major' && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-red-500 to-red-400" />
                )}
                {pulse.magnitude === 'moderate' && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-amber-500 to-amber-400" />
                )}
                
                <div className="flex items-start justify-between gap-4 pl-2">
                  <div 
                    className="flex-1 min-w-0"
                    onClick={() => onPulseClick(pulse)}
                  >
                    {/* Impact indicators row */}
                    <div className="flex items-center gap-3 mb-2">
                      <ImpactIndicator 
                        magnitude={pulse.magnitude} 
                        scope={pulse.scope}
                        timeSensitivity={pulse.timeSensitivity}
                      />
                      {isLowRelevance && (
                        <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded-full">
                          Low Relevance
                        </span>
                      )}
                    </div>
                    
                    {/* Title */}
                    <h3 className={cn(
                      "font-semibold text-sm mb-2 line-clamp-2 transition-colors",
                      isLowRelevance 
                        ? "text-neutral-600 dark:text-neutral-400 group-hover:text-neutral-800"
                        : "text-neutral-900 dark:text-white group-hover:text-primary"
                    )}>
                      {pulse.title}
                    </h3>
                    
                    {/* TL;DR or fallback to whatChanged */}
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 mb-3 leading-relaxed">
                      {pulse.tldrShort || pulse.whatChanged}
                    </p>
                    
                    {/* Meta row */}
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-medium text-neutral-700 dark:text-neutral-300">
                        {pulse.date}
                      </span>
                      <span className="w-1 h-1 bg-neutral-300 dark:bg-neutral-600 rounded-full" />
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] font-medium px-2 py-0.5",
                          pulse.status === "Under Review" &&
                            "border-yellow-400 text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20",
                          pulse.status === "Public Consultation" &&
                            "border-blue-400 text-blue-700 bg-blue-50 dark:bg-blue-900/20",
                          pulse.status === "Approved" &&
                            "border-green-400 text-green-700 bg-green-50 dark:bg-green-900/20"
                        )}
                      >
                        {pulse.status}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePin(pulse);
                      }}
                      className={cn(
                        "h-8 w-8 p-0 rounded-full transition-all",
                        isPinned
                          ? "text-primary bg-primary/10 hover:bg-primary/20"
                          : "text-neutral-400 hover:text-primary hover:bg-primary/5"
                      )}
                    >
                      <Pin
                        className={cn("h-4 w-4", isPinned && "fill-current")}
                      />
                    </Button>
                    <ChevronRight 
                      className="h-4 w-4 text-neutral-300 group-hover:text-primary transition-colors"
                      onClick={() => onPulseClick(pulse)}
                    />
                  </div>
                </div>
              </div>
            );
          });
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
